import { useState, useEffect, useRef } from 'react'
import { Broadcast } from '../../types'

interface AudioPlayerProps {
  streamUrl: string
  fallbackUrl?: string
  broadcast: Broadcast | null
}

// Player is ALWAYS enabled - YesStreaming provides AutoDJ when no live broadcast
export function AudioPlayer({ streamUrl, fallbackUrl, broadcast }: AudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isConnected, setIsConnected] = useState(false)
  const [volume, setVolume] = useState(80)
  const [error, setError] = useState<string | null>(null)

  const audioRef = useRef<HTMLAudioElement | null>(null)
  const usingFallback = useRef(false)
  const isPlayingRef = useRef(false)

  // Keep ref in sync so Media Session handlers don't stale-close over state
  useEffect(() => {
    isPlayingRef.current = isPlaying
  }, [isPlaying])

  // Update volume when changed
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100
    }
  }, [volume])

  // Setup audio element event listeners
  useEffect(() => {
    const audio = audioRef.current
    if (!audio) return

    const handlePlaying = () => {
      console.log('Audio playing')
      setIsConnected(true)
      setIsConnecting(false)
      setIsPlaying(true)
      setError(null)
    }

    const handlePause = () => {
      setIsPlaying(false)
    }

    const handleError = (e: Event) => {
      console.error('Audio error:', e)

      // Try fallback URL if available and not already using it
      if (fallbackUrl && !usingFallback.current && audio.src !== fallbackUrl) {
        console.log('Primary stream failed, trying fallback:', fallbackUrl)
        usingFallback.current = true
        audio.src = fallbackUrl
        audio.load()
        audio.play().catch(err => {
          console.error('Fallback also failed:', err)
          setError('לא ניתן להתחבר לשידור')
          setIsConnecting(false)
          setIsConnected(false)
          setIsPlaying(false)
        })
      } else {
        setError('החיבור לשידור נכשל')
        setIsConnecting(false)
        setIsConnected(false)
        setIsPlaying(false)
      }
    }

    const handleWaiting = () => {
      console.log('Audio buffering...')
    }

    const handleCanPlay = () => {
      console.log('Audio can play')
    }

    audio.addEventListener('playing', handlePlaying)
    audio.addEventListener('pause', handlePause)
    audio.addEventListener('error', handleError)
    audio.addEventListener('waiting', handleWaiting)
    audio.addEventListener('canplay', handleCanPlay)

    return () => {
      audio.removeEventListener('playing', handlePlaying)
      audio.removeEventListener('pause', handlePause)
      audio.removeEventListener('error', handleError)
      audio.removeEventListener('waiting', handleWaiting)
      audio.removeEventListener('canplay', handleCanPlay)
    }
  }, [fallbackUrl])

  // Media Session API — lock screen / notification controls
  useEffect(() => {
    if (!('mediaSession' in navigator)) return

    const title = broadcast?.show_name || 'RADIO NATKA'
    const artist = broadcast?.broadcaster_name || (broadcast?.is_live ? '' : 'Auto DJ')
    const album = 'RADIO NATKA'

    const artwork = [
      { src: `${window.location.origin}/icons/icon-192.png`, sizes: '192x192', type: 'image/png' },
      { src: `${window.location.origin}/icons/icon-512.png`, sizes: '512x512', type: 'image/png' },
    ]

    navigator.mediaSession.metadata = new MediaMetadata({ title, artist, album, artwork })

    navigator.mediaSession.setActionHandler('play', () => {
      if (!isPlayingRef.current) {
        audioRef.current?.play().catch(console.error)
      }
    })

    navigator.mediaSession.setActionHandler('pause', () => {
      if (isPlayingRef.current) {
        audioRef.current?.pause()
        setIsPlaying(false)
        setIsConnected(false)
      }
    })

    // Live radio — no seeking or skipping
    navigator.mediaSession.setActionHandler('seekbackward', null)
    navigator.mediaSession.setActionHandler('seekforward', null)
    navigator.mediaSession.setActionHandler('previoustrack', null)
    navigator.mediaSession.setActionHandler('nexttrack', null)
  }, [broadcast])

  // Sync Media Session playback state
  useEffect(() => {
    if (!('mediaSession' in navigator)) return
    navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused'
  }, [isPlaying])

  const handlePlayPause = async () => {
    if (!streamUrl) {
      setError('כתובת השידור לא זמינה')
      return
    }

    const audio = audioRef.current
    if (!audio) return

    if (isPlaying) {
      // Pause
      audio.pause()
      setIsPlaying(false)
      setIsConnected(false)
    } else {
      // Play - stream is always available (live broadcast or AutoDJ)
      setIsConnecting(true)
      setError(null)
      usingFallback.current = false

      try {
        // Set stream URL and play
        audio.src = streamUrl
        audio.load()
        await audio.play()
        console.log('Connected to stream:', streamUrl)
      } catch (err: any) {
        console.error('Error playing stream:', err)

        // Try fallback
        if (fallbackUrl) {
          console.log('Trying fallback URL:', fallbackUrl)
          usingFallback.current = true
          try {
            audio.src = fallbackUrl
            audio.load()
            await audio.play()
            console.log('Connected to fallback stream:', fallbackUrl)
          } catch (fallbackErr: any) {
            console.error('Fallback also failed:', fallbackErr)
            setError('לא ניתן להתחבר לשידור')
            setIsConnecting(false)
          }
        } else {
          setError('לא ניתן להתחבר לשידור')
          setIsConnecting(false)
        }
      }
    }
  }

  return (
    <div className="flex flex-col items-center gap-4 sm:gap-6">
      {/* Audio element (hidden) */}
      <audio ref={audioRef} />

      {/* Play/Pause Button - Always enabled */}
      <button
        onClick={handlePlayPause}
        disabled={!streamUrl || isConnecting}
        className={`
          w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center text-3xl sm:text-4xl
          transition-all duration-200 shadow-lg active:scale-95
          ${streamUrl
            ? 'bg-[var(--accent)] hover:bg-[var(--accent-hover)] cursor-pointer'
            : 'bg-[var(--bg-tertiary)] cursor-not-allowed opacity-50'
          }
          ${isConnected ? 'sm:scale-110' : 'scale-100'}
        `}
        aria-label={isPlaying ? 'Pause' : 'Play'}
      >
        {isConnecting ? (
          <span className="animate-spin">⏳</span>
        ) : isPlaying ? (
          '⏸️'
        ) : (
          '▶️'
        )}
      </button>

      {/* Status */}
      <div className="text-center px-4">
        {error && (
          <p className="text-red-500 text-xs sm:text-sm mb-2">שגיאה: {error}</p>
        )}
        {isConnecting && (
          <p className="text-[var(--text-secondary)] text-xs sm:text-sm">מתחבר...</p>
        )}
        {isConnected && !error && (
          <div className="flex items-center gap-2 justify-center">
            <span className="w-2 h-2 bg-[var(--success)] rounded-full animate-pulse-slow"></span>
            <p className="text-[var(--success)] text-xs sm:text-sm font-medium">מחובר לשידור</p>
          </div>
        )}
      </div>

      {/* Volume Control - hidden on mobile */}
      <div className="hidden md:block w-full max-w-xs px-4 sm:px-0">
        <div className="flex items-center gap-2 sm:gap-3">
          <span className="text-base sm:text-lg">🔊</span>
          <input
            type="range"
            min="0"
            max="100"
            value={volume}
            onChange={(e) => setVolume(Number(e.target.value))}
            className="flex-1 h-2 bg-[var(--bg-tertiary)] rounded-lg appearance-none cursor-pointer accent-[var(--accent)]"
            aria-label="Volume"
          />
          <span className="text-xs sm:text-sm text-[var(--text-secondary)] w-8 sm:w-10 text-left">
            {volume}%
          </span>
        </div>
      </div>
    </div>
  )
}
